import {
  SessionSummary,
  SessionMajorVersion,
  SessionMajorVersions,
} from "src/store/api/generatedApi";
import { sum, toggleMember } from "helpers";
import { z } from "zod";

const SessionVersionsList = z.array(
  z.object({ session_id: z.int(), versions: z.array(z.unknown()) })
);

export const versionsBySessionId = (
  response: SessionMajorVersions[] | undefined
): ReadonlyMap<number, SessionMajorVersion[]> =>
  response !== undefined && SessionVersionsList.safeParse(response).success
    ? new Map(
        response.map(({ session_id, versions }) => [session_id, versions])
      )
    : new Map();

export interface OpenSessions {
  base: "matching" | "all" | "none";
  toggled: ReadonlySet<number>;
}

export type OpenSessionsAction =
  | { type: "toggle"; sessionId: number }
  | { type: "openMatching" }
  | { type: "expandAll" }
  | { type: "collapseAll" };

export const openSessionsReducer = (
  state: OpenSessions,
  action: OpenSessionsAction
): OpenSessions => {
  switch (action.type) {
    case "toggle":
      return {
        ...state,
        toggled: toggleMember(state.toggled, action.sessionId),
      };
    case "openMatching":
      return { base: "matching", toggled: new Set() };
    case "expandAll":
      return { base: "all", toggled: new Set() };
    case "collapseAll":
      return { base: "none", toggled: new Set() };
  }
};

export interface SessionMatcher {
  matchesName: (session: SessionSummary) => boolean;
  matchesSessionRow: (session: SessionSummary) => boolean;
  matchesVersion: (
    session: SessionSummary,
    version: SessionMajorVersion
  ) => boolean;
}

export type SearchesGridRow =
  | {
      kind: "session";
      id: string;
      session: SessionSummary;
      versionCount: number;
      listedVersionCount: number;
      isOpen: boolean;
    }
  | {
      kind: "version";
      id: string;
      session: SessionSummary;
      version: SessionMajorVersion;
      isLastVersion: boolean;
    };

export interface SearchesGrid {
  rows: SearchesGridRow[];
  sessionCount: number;
  versionCount: number;
  canOpenAnySession: boolean;
}

const listedVersions = (
  session: SessionSummary,
  versions: SessionMajorVersion[],
  matcher: SessionMatcher | undefined
): SessionMajorVersion[] | null => {
  if (matcher === undefined || matcher.matchesName(session)) {
    return versions;
  }
  const matchingVersions = versions.filter((version) =>
    matcher.matchesVersion(session, version)
  );
  if (matchingVersions.length > 0) {
    return matchingVersions;
  }
  return matcher.matchesSessionRow(session) ? versions : null;
};

export const listSearchesGrid = ({
  sessions,
  versionsBySession,
  openSessions,
  matcher,
}: {
  sessions: SessionSummary[];
  versionsBySession: ReadonlyMap<number, SessionMajorVersion[]>;
  openSessions: OpenSessions;
  matcher: SessionMatcher | undefined;
}): SearchesGrid => {
  const shownSessions = sessions.flatMap((session) => {
    const versions = versionsBySession.get(session.session_id) ?? [];
    const listed = listedVersions(session, versions, matcher);
    return listed === null ? [] : [{ session, versions, listed }];
  });
  const rows = shownSessions.flatMap(({ session, versions, listed }) => {
    const canOpen = versions.length > 1;
    const opensWhenMatching = listed.length < versions.length;
    const isOpenByDefault =
      openSessions.base === "all" ||
      (openSessions.base === "matching" && opensWhenMatching);
    const isOpen =
      canOpen &&
      isOpenByDefault !== openSessions.toggled.has(session.session_id);
    const sessionRow: SearchesGridRow = {
      kind: "session",
      id: `session-${session.session_id}`,
      session,
      versionCount: versions.length,
      listedVersionCount: listed.length,
      isOpen,
    };
    const versionRows = isOpen
      ? listed.map((version, index): SearchesGridRow => ({
          kind: "version",
          id: `version-${version.search_id}`,
          session,
          version,
          isLastVersion: index === listed.length - 1,
        }))
      : [];
    return [sessionRow, ...versionRows];
  });
  return {
    rows,
    sessionCount: shownSessions.length,
    versionCount: sum(shownSessions.map(({ versions }) => versions.length)),
    canOpenAnySession: shownSessions.some(
      ({ versions }) => versions.length > 1
    ),
  };
};
